import { Box, DialogContent, Modal } from "@mui/material"
import {
  FocusContext,
  setFocus,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation"
import React, { useEffect, useRef } from "react"
import { SettingsFocusKeys } from "src/screens/Settings/settings-focus-keys"

import { DGText } from "../../components/common/DGText"
import { Footer } from "../../components/common/Footer"
import { useModalDismiss } from "../../hooks/use-modal-dismiss"
import { useTransitions } from "../../hooks/use-transitions"
import { useAuth } from "../../store/AppStore"
import { comb, s } from "../../utilities/common"
import { env, platform, version } from "../../utilities/constants"
import { theme, TI } from "../../utilities/theme"

import { SettingsButton } from "./components/SettingsButton"

export const DebugModal = ({
  open,
  returnFocus,
}: {
  open: comb<boolean>
  returnFocus: string
}) => {
  if (!open.val) return null
  return <Screen open={open} returnFocus={returnFocus} />
}

const Screen = ({
  open,
  returnFocus,
}: {
  open: comb<boolean>
  returnFocus: string
}) => {
  const scrollRef = useRef<HTMLElement>(null)

  const { ref, focusKey } = useFocusable({
    isFocusBoundary: true,
  })

  const { logout } = useAuth()

  useModalDismiss(() => {
    setFocus(returnFocus)
    open.set(false)
  })

  useEffect(() => {
    setFocus(SettingsFocusKeys.DebugDefault)
  }, [setFocus])

  const transition = useTransitions()

  return (
    <Modal
      open={open.val}
      sx={{
        height: "100vh",
        width: "100vw",
        backgroundColor: theme.palette.primary.variant,
        "& > *": { transition: "none !important" },
      }}
    >
      <DialogContent>
        <Box
          ref={scrollRef}
          style={{ transition }}
          sx={{
            height: "auto",
            minHeight: "100%",
            width: "100%",
            backgroundColor: theme.palette.primary.variant,
            position: "absolute",
            overflowY: "visible",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <FocusContext.Provider value={focusKey}>
              <Box
                ref={ref}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  width: s("1160px"),
                  mt: s("184px"),
                  "& > :not(:last-child)": {
                    mb: s("60px"),
                  },
                }}
              >
                <SettingsButton
                  action={() => {
                    if (env.env === "staging")
                      window.localStorage.removeItem("env")
                    else window.localStorage.setItem("env", "staging")
                    document.getElementById("root")!.style.transform =
                      "translate3d(0, 0, 0)"
                    setTimeout(() => {
                      window.location.reload()
                    }, 300)
                  }}
                  title={
                    "Switch to " +
                    (env.env === "staging" ? "Production" : "Staging") +
                    " (Backend)"
                  }
                  focusKey={SettingsFocusKeys.DebugDefault}
                  scrollRef={scrollRef}
                />
                <SettingsButton
                  action={() => {
                    const host = window.location.host
                    if (platform === "WebOS") {
                      if (host.includes("staging"))
                        window.location.href =
                          "http://lg-smarttv.stage-plus.com"
                      else
                        window.location.href =
                          "http://lg-smarttv.staging.stage-plus.com"
                    } else if (platform === "Tizen") {
                      if (host.includes("staging"))
                        window.location.href =
                          "http://samsung-smarttv.stage-plus.com"
                      else
                        window.location.href =
                          "http://samsung-smarttv.staging.stage-plus.com"
                    } else if (platform === "web") {
                      if (host.includes("localhost"))
                        window.location.href = "https://staging.dg.evenly.app/"
                      else window.location.href = "http://localhost:3000"
                    }
                  }}
                  title={
                    "Switch to " +
                    (window.location.host.includes("staging")
                      ? "Production"
                      : "Staging") +
                    " (Frontend)"
                  }
                  focusKey={SettingsFocusKeys.DebugFrontendSwitch}
                  scrollRef={scrollRef}
                />
                <SettingsButton
                  focusKey={SettingsFocusKeys.DebugDisable}
                  action={() => {
                    window.localStorage.removeItem("debugMode")
                    window.location.reload()
                  }}
                  title={"Disable Debug"}
                  scrollRef={scrollRef}
                />
                <SettingsButton
                  focusKey={SettingsFocusKeys.RestartTour}
                  action={() => {
                    logout()
                    window.localStorage.removeItem("onboardingPlaceholder")
                    document.getElementById("root")!.style.transform =
                      "translate3d(0, 0, 0)"
                    setTimeout(() => {
                      window.location.reload()
                    }, 300)
                  }}
                  title={"Logout & Restart Tour"}
                  scrollRef={scrollRef}
                />
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <DGText
                    tx={TI.Subheadline06tv}
                    sx={{
                      color: theme.palette.white.main,
                      fontStyle: "italic",
                    }}
                  >
                    Platform: {platform}
                  </DGText>
                  <DGText
                    tx={TI.Subheadline06tv}
                    sx={{
                      color: theme.palette.white.main,
                      fontStyle: "italic",
                    }}
                  >
                    Version: {version}
                  </DGText>
                  <DGText
                    tx={TI.Subheadline06tv}
                    sx={{
                      color: theme.palette.white.main,
                      fontStyle: "italic",
                    }}
                  >
                    Build Date/Time: {process.env.REACT_APP_BUILDINFO ?? ""}
                  </DGText>
                </Box>
              </Box>
            </FocusContext.Provider>
          </Box>
          <Footer />
        </Box>
      </DialogContent>
    </Modal>
  )
}
